import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { FLOATING_ELEMENT_PLACEMENT } from '../../../constants/common'
import { WEBAPP_ROUTE_PREFIX } from '../../../constants/route'
import {
  useFloatingSelect,
  useIsExternalContactProfileNGAvailable,
  useIsProviderProfileNGAvailable,
  useIsTeamVisibilityHidden,
  useIsUserProfileNGAvailable,
  useLinkLikeClick,
  useOutboundUuid,
  useUserData,
} from '../../../hooks'
import {
  getUserProfileRoute,
  isStaffUser,
  isTeamUserProfileHidden,
} from '../../../utils/user'
import { FloatingSelectControl, FloatingSelectDropdown } from '../../Floating'
import { ProfileUserInfoItemCard } from '../ProfileUserInfoItemCard'
import { ProfileUsersWidgetItem } from '../ProfileUsersWidgetItem'

import { PROFILE_USER_INFO_ITEM_TOOLTIP_PROPS } from './ProfileUserInfoItem.constants'
import {
  useProfileUserInfoItemHover,
  useProfileUserInfoTooltipData,
} from './ProfileUserInfoItem.hooks'
import { getProfileUserCardDataFromUserInfoTooltip } from './ProfileUserInfoItem.utils'

import * as styles from './ProfileUserInfoItem.module.css'

function ProfileUserInfoItem({
  Card = ProfileUserInfoItemCard,
  blocked,
  iconsProps,
  tooltipProps = PROFILE_USER_INFO_ITEM_TOOLTIP_PROPS,
  value,
  ...restProps
}) {
  const { t } = useTranslation()

  // #INFO Connected (iDWELL-to-iDWELL) requests are read-only: their users
  // aren't resolvable via the regular endpoints, so the hover tooltip (and its
  // fetch) is disabled and navigation is blocked. `outboundUuid` is read once
  // here and the resulting `blocked` is passed down to the nested item, so the
  // child doesn't need to read the route param itself.
  const isInteractionDisabled = Boolean(useOutboundUuid())

  const isTeamHidden = useIsTeamVisibilityHidden()

  const currentUser = useUserData()
  const canViewUserInfoTooltip =
    isStaffUser(currentUser) && !isInteractionDisabled

  const isHiddenTeamUser = isTeamUserProfileHidden({
    currentUser,
    isTeamHidden,
    user: value,
  })

  const { handleClick, handleMouseDown } = useLinkLikeClick()

  const isExternalContactProfileNGAvailable =
    useIsExternalContactProfileNGAvailable()
  const isProviderProfileNGAvailable = useIsProviderProfileNGAvailable()
  const isUserProfileNGAvailable = useIsUserProfileNGAvailable()

  const hoverContainerRef = useRef(null)

  const {
    context,
    floatingStyles,
    getFloatingProps,
    isOpen,
    refs: floatingRefs,
    setOpen,
  } = useFloatingSelect({
    placement: FLOATING_ELEMENT_PLACEMENT.topStart,
  })

  const { userInfoTooltipData, userInfoTooltipUserId } =
    useProfileUserInfoTooltipData({
      enabled: canViewUserInfoTooltip,
      isOpen,
      userId: value?.id,
    })

  const cardData = getProfileUserCardDataFromUserInfoTooltip(
    userInfoTooltipData,
    t
  )

  const {
    handleIconMouseEnter,
    handleIconMouseLeave,
    handleMouseEnter,
    handleMouseLeave,
  } = useProfileUserInfoItemHover({
    hoverContainerRef,
    setOpen,
  })

  const link = getUserProfileRoute({
    id: value?.id,
    isExternalContactProfileNGAvailable,
    isProviderProfileNGAvailable,
    isUserProfileNGAvailable,
    role: value?.role,
  })

  function handleOpenProfile(event) {
    if (value?.id) {
      handleClick({
        event,
        link,
        // Webapp fallback keeps opening in a new tab; NG profiles navigate in-app.
        outsideLink: link.includes(WEBAPP_ROUTE_PREFIX),
      })
    }
  }

  function handleMouseDownProfile(event) {
    if (value?.id) {
      handleMouseDown({ event, link })
    }
  }

  const isCardVisible =
    canViewUserInfoTooltip &&
    isOpen &&
    userInfoTooltipUserId === value?.id &&
    !!cardData

  return canViewUserInfoTooltip ? (
    <>
      <FloatingSelectControl context={context}>
        <div
          className={styles.container}
          ref={hoverContainerRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <ProfileUsersWidgetItem
            {...restProps}
            blocked={blocked || isInteractionDisabled || isHiddenTeamUser}
            iconsProps={{
              ...iconsProps,
              onIconMouseEnter: handleIconMouseEnter,
              onIconMouseLeave: handleIconMouseLeave,
            }}
            tooltipProps={tooltipProps}
            value={value}
          />
        </div>
      </FloatingSelectControl>
      <FloatingSelectDropdown
        context={context}
        floatingStyles={floatingStyles}
        getFloatingProps={getFloatingProps}
        isOpen={isCardVisible}
        refs={floatingRefs}
      >
        {Card ? (
          <Card
            cardData={cardData}
            onMouseDownProfile={handleMouseDownProfile}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onOpenProfile={handleOpenProfile}
          />
        ) : null}
      </FloatingSelectDropdown>
    </>
  ) : (
    <ProfileUsersWidgetItem
      {...restProps}
      blocked={blocked || isInteractionDisabled || isHiddenTeamUser}
      iconsProps={iconsProps}
      value={value}
    />
  )
}

export default ProfileUserInfoItem
